import isStrongPassword from 'validator/lib/isStrongPassword';

// From https://github.com/validatorjs/validator.js
interface StrongPasswordOptions {
  minLength: number;
  minLowercase: number;
  minUppercase: number;
  minNumbers: number;
  minSymbols: number;
}

// OA: The password hint text is hard coded, so remember to update it (or make it dynamic) if you edit
// these. Also, you'll have to update the RegEx tests in the "getPasswordValidationInfo" function if you increase
// others than minLength to more than 1.
export const passwordRules: StrongPasswordOptions = {
  minLength: 8,
  minLowercase: 1,
  minUppercase: 1,
  minNumbers: 1,
  minSymbols: 1,
};

export function isValidPassword(password: string | undefined, email: string | undefined, username?: string): boolean {
  if (!password) return false;
  return isStrongPassword(password, passwordRules) && !isSameAsEmailOrUsername(password, email, username);
}

function isSameAsEmailOrUsername(password: string, email?: string, username?: string) {
  const isSameAsEmail = email ? password.toLowerCase() === email.toLowerCase() : false;
  const isSameAsUsername = username ? password.toLowerCase() === username.toLowerCase() : false;
  return password.length && (isSameAsEmail || isSameAsUsername);
}

/**
 * Used to tell the user which parts of the validation their input did/didn't pass.
 */
export interface PasswordValidationInfo extends PasswordValidationInfoBase {
  notSameAsEmailOrUsername: boolean;
}
type PasswordValidationInfoBase = { [k in keyof StrongPasswordOptions]: boolean | undefined };

export function getPasswordValidationInfo(
  password: string | undefined,
  email: string | undefined,
  username?: string,
): PasswordValidationInfo {
  if (!password) {
    return {
      minLength: false,
      minLowercase: false,
      minUppercase: false,
      minNumbers: false,
      minSymbols: false,
      notSameAsEmailOrUsername: true,
    };
  }
  const minLength = password.length >= passwordRules.minLength;

  // OA: For consistency, these rules are adapted from
  // https://github.com/validatorjs/validator.js/blob/master/src/lib/isStrongPassword.js
  // (from validator.js v13.6.0)
  const minLowercase = /[a-z]/.test(password);
  const minUppercase = /[A-Z]/.test(password);
  const minNumbers = /[0-9]/.test(password);
  // eslint-disable-next-line no-useless-escape
  const minSymbols = /[-#!$@%^&*()_+|~=`{}\[\]:";'<>?,.\/ ]/.test(password);

  return {
    minLength,
    minLowercase,
    minUppercase,
    minNumbers,
    minSymbols,
    notSameAsEmailOrUsername: !isSameAsEmailOrUsername(password, email, username),
  };
}
